import React from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../contexts/app-context';
import { AuthContext } from '../contexts/auth-context';
import { useAllianceIdentity } from '../hooks/use-alliance-identity';
import { useSessionActions } from '../hooks/use-session-actions';
import {
  AllianceAppBar,
  AllianceAppBarSpacer,
} from './alliance/alliance-app-bar';
import { ConnectionBanner, UpdatedBanner } from './alliance/app-banners';
import { AppNavDrawer } from './alliance/app-nav-drawer';
import { SessionExpiredDialog } from './alliance/session-expired-dialog';
import { UserControl } from './alliance/user-control';
import { NavDrawerBody, NavTopBar, useNavLinks } from './navigation';
import { SignInBarButton } from './sign-in-bar-button';

/** Nav drawer width (design: 300px) */
const NAV_DRAWER_WIDTH = 300;

/**
 * Below these widths the nav links fold into the drawer (measured with the
 * real fonts, plus room for a long name in the user control)
 */
const NAV_BREAKPOINT = { admin: 1000, default: 920 };

/**
 * Events' App Bar: brand (links Home), the Oswald top nav (wide screens) or
 * the menu button and nav drawer (narrower, see NAV_BREAKPOINT), then either
 * the Sign in button (anonymous visitors: no launcher, no user control) or
 * the user control (Sign out and RecruitLink) and the App Launcher.
 * Rendered once by the shell (routes/root.tsx). The bar is fixed, so a
 * spacer holds its place.
 *
 * Also mounts what every page needs once: the connection and "updated"
 * banner sources (shown by <AppBanners /> under the bar) and the
 * session-expired dialog.
 */
export function AppShellBar() {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  // Hooks
  const location = useLocation();
  const links = useNavLinks();
  const identity = useAllianceIdentity();
  const { signOut, signInAgain } = useSessionActions();

  const closeDrawer = React.useCallback(
    () => dispatch({ type: 'DRAWER_CLOSE' }),
    [dispatch],
  );

  React.useEffect(() => {
    // Close the drawer once a link has been followed
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  const showsAdmin = links.some((link) => link.to.startsWith('/admin'));

  const isAuthenticated = authContext.state.user !== undefined;

  return (
    <React.Fragment>
      <AllianceAppBar
        homeTo="/"
        loading={state.routeLoadingCount > 0}
        nav={<NavTopBar links={links} />}
        navBreakpoint={
          showsAdmin ? NAV_BREAKPOINT.admin : NAV_BREAKPOINT.default
        }
        onMenuClick={() => dispatch({ type: 'TOGGLE_DRAWER' })}
        onMenuClose={closeDrawer}
        menuOpen={state.drawerOpen}
        user={
          identity ? (
            <UserControl identity={identity} onSignOut={signOut} />
          ) : (
            <SignInBarButton />
          )
        }
        launcher={isAuthenticated}
      />
      <AllianceAppBarSpacer />

      <AppNavDrawer
        permanent={false}
        width={NAV_DRAWER_WIDTH}
        homeTo="/"
        open={state.drawerOpen}
        onClose={closeDrawer}
      >
        <NavDrawerBody links={links} />
      </AppNavDrawer>

      {/* Banner sources: each keeps its banner in the queue while it applies */}
      <ConnectionBanner />
      <UpdatedBanner pathname={location.pathname} />

      <SessionExpiredDialog
        open={authContext.state.tokenError}
        onSignIn={() => signInAgain().catch(() => undefined)}
        onSignOut={signOut}
      />
    </React.Fragment>
  );
}
